/**
 * Виды документов (раздел 4 спецификации подпроекта 4): оферта студии, заявка по оферте и её приложение —
 * спецификация, счёт, акт, допсоглашение, договоры с сотрудником по форме работы и акт о выплате.
 */
export const DOCUMENT_KINDS = [
  'offer',
  'order',
  'specification',
  'invoice',
  'act',
  'amendment',
  'employee_npd',
  'employee_ip',
  'employee_gph',
  'employee_staff',
  'payout_act',
  'subscription_offer',
  'privacy_policy',
  'pd_consent',
] as const;
export type DocumentKind = (typeof DOCUMENT_KINDS)[number];

/**
 * Публичные документы: оферта и документы сайта (подпроект 12) — публикуются версиями в настройках со снимком
 * реквизитов студии, не подписываются, не выдаются клиенту и вручную не загружаются. Сайт показывает действующую версию.
 */
export const PUBLIC_KINDS = [
  'offer',
  'subscription_offer',
  'privacy_policy',
  'pd_consent',
] as const satisfies readonly DocumentKind[];
export type PublicKind = (typeof PUBLIC_KINDS)[number];
export const isPublicKind = (kind: string): kind is PublicKind =>
  (PUBLIC_KINDS as readonly string[]).includes(kind);

/** Документы сайта: страницы глори.digital, а не страницы КП. */
export const SITE_KINDS = [
  'subscription_offer',
  'privacy_policy',
  'pd_consent',
] as const satisfies readonly PublicKind[];
export type SiteKind = (typeof SITE_KINDS)[number];

/** Договор с сотрудником — по форме работы: самозанятый, ИП, физлицо по ГПХ, штат. */
export const EMPLOYEE_CONTRACT_KINDS = [
  'employee_npd',
  'employee_ip',
  'employee_gph',
  'employee_staff',
] as const satisfies readonly DocumentKind[];

/**
 * Вид загруженного вручную документа: виды платформы и «Другое» — скан, который платформа не собирает
 * (договор аренды, NDA, бумажный договор со старым клиентом). Оферту вручную не загружают: она публикуется
 * в настройках.
 */
export const STORED_KINDS = [...DOCUMENT_KINDS, 'other'] as const;
export type StoredKind = (typeof STORED_KINDS)[number];
export const UPLOAD_KINDS = STORED_KINDS.filter(
  (kind): kind is Exclude<StoredKind, PublicKind> => !isPublicKind(kind),
);
export type UploadKind = (typeof UPLOAD_KINDS)[number];

/**
 * Кто подписывает документ этого вида: никто (счёт), только студия, только другая сторона или обе по очереди.
 * Подписанты идут строго по очереди; подпись последнего завершает пакет (раздел 5).
 */
export const SIGNING_MODES = ['none', 'studio', 'party', 'studio_then_party', 'party_then_studio'] as const;
export type SigningMode = (typeof SIGNING_MODES)[number];

/**
 * Серия нумерации: сквозной номер по серии и году студии. У договоров с сотрудниками одна серия на все формы
 * работы; у оферты (номер — версия), спецификации («Приложение № 1 к заявке») и допсоглашения («№ N к заявке»)
 * своей серии нет.
 */
export const NUMBER_SERIES = ['order', 'invoice', 'act', 'employee_contract', 'payout_act'] as const;
export type NumberSeries = (typeof NUMBER_SERIES)[number];

interface KindRule {
  /** Кто подписывает по умолчанию (раздел 17). */
  readonly signing: SigningMode;
  /** Меняет ли админ «Кто подписывает»: у оферты и спецификации порядок задан самим документом. */
  readonly signingEditable: boolean;
  readonly series: NumberSeries | null;
  /** С кем документ: клиент сделки или сотрудник. */
  readonly party: 'client' | 'employee' | null;
}

const RULES: Readonly<Record<DocumentKind, KindRule>> = {
  offer: { signing: 'none', signingEditable: false, series: null, party: null },
  order: { signing: 'studio_then_party', signingEditable: true, series: 'order', party: 'client' },
  // Спецификация — приложение к заявке: подписывается вместе с ней и в её порядке.
  specification: { signing: 'studio_then_party', signingEditable: false, series: null, party: 'client' },
  invoice: { signing: 'none', signingEditable: true, series: 'invoice', party: 'client' },
  act: { signing: 'studio_then_party', signingEditable: true, series: 'act', party: 'client' },
  amendment: { signing: 'studio_then_party', signingEditable: true, series: null, party: 'client' },
  employee_npd: {
    signing: 'party_then_studio',
    signingEditable: true,
    series: 'employee_contract',
    party: 'employee',
  },
  employee_ip: {
    signing: 'party_then_studio',
    signingEditable: true,
    series: 'employee_contract',
    party: 'employee',
  },
  employee_gph: {
    signing: 'party_then_studio',
    signingEditable: true,
    series: 'employee_contract',
    party: 'employee',
  },
  employee_staff: {
    signing: 'party_then_studio',
    signingEditable: true,
    series: 'employee_contract',
    party: 'employee',
  },
  payout_act: {
    signing: 'studio_then_party',
    signingEditable: true,
    series: 'payout_act',
    party: 'employee',
  },
  subscription_offer: { signing: 'none', signingEditable: false, series: null, party: null },
  privacy_policy: { signing: 'none', signingEditable: false, series: null, party: null },
  pd_consent: { signing: 'none', signingEditable: false, series: null, party: null },
};

export const defaultSigning = (kind: DocumentKind): SigningMode => RULES[kind].signing;
export const signingEditable = (kind: DocumentKind): boolean => RULES[kind].signingEditable;
export const numberSeries = (kind: DocumentKind): NumberSeries | null => RULES[kind].series;
export const partyOf = (kind: DocumentKind): 'client' | 'employee' | null => RULES[kind].party;

export function isDocumentKind(value: string): value is DocumentKind {
  return (DOCUMENT_KINDS as readonly string[]).includes(value);
}

export function isSigningMode(value: string): value is SigningMode {
  return (SIGNING_MODES as readonly string[]).includes(value);
}

/**
 * Состояние документа: черновик (собирается, удаляется), заморожен (номер, данные и хэш не меняются — ждёт
 * подписи или загружен без отметки «подписан»), подписан, отозван. Удаляются только черновики.
 */
export const DOCUMENT_STATUSES = ['draft', 'frozen', 'signed', 'void'] as const;
export type DocumentStatus = (typeof DOCUMENT_STATUSES)[number];

/** Откуда документ: собран платформой из шаблона или загружен вручную (скан). */
export const DOCUMENT_ORIGINS = ['generated', 'uploaded'] as const;
export type DocumentOrigin = (typeof DOCUMENT_ORIGINS)[number];

/** Состояние версии текста: черновик правится, опубликованная неизменна (правами БД). */
export const TEMPLATE_VERSION_STATUSES = ['draft', 'published'] as const;
export type TemplateVersionStatus = (typeof TEMPLATE_VERSION_STATUSES)[number];
